import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';

import {
    MenuOutlined,
    BellOutlined,
    CheckOutlined,
    CheckCircleOutlined,
    ArrowLeftOutlined
} from '@ant-design/icons';

import {
    Badge,
    Button,
    Drawer,
    Empty,
    List,
    Spin,
    Tabs,
    Tag,
    Typography,
    message
} from 'antd';

import axios from 'axios';

import Sidebar from './Sidebar';
import InstallerBottomNav from './InstallerBottomNav';
import InstallerPhotoFab from './InstallerPhotoFab';
import PreviewModeBanner from './PreviewModeBanner';

import {
    Link,
    useLocation,
    useNavigate
} from 'react-router-dom';

import '../styles/InstallerWorkHub.css';

import {
    ensureBrowserNotifyPermission,
    playNotificationBeep,
    showBrowserNotification
} from '../utils/notificationAlerts';
import { formatMoscowClock } from '../utils/taskManager';

const {
    Text
} = Typography;

/** «Новое» — непрочитанные за последние 48 часов */
const NEW_NOTIFICATION_MS =
    48 * 60 * 60 * 1000;

/** Совпавшие ВДР («зелёные») — отдельная вкладка «Совпало» */
const OK_NOTIFICATION_TYPE =
    'gepard_income_ok';

const Layout = ({
    children,
    fullHeight = false
}) => {
    const [
        isSidebarOpen,
        setSidebarOpen
    ] = useState(false);

    const [
        notificationOpen,
        setNotificationOpen
    ] = useState(false);

    const [
        notifications,
        setNotifications
    ] = useState([]);

    const [
        unreadCount,
        setUnreadCount
    ] = useState(0);

    const [
        notificationLoading,
        setNotificationLoading
    ] = useState(false);

    const [
        notificationTab,
        setNotificationTab
    ] = useState('new');

    const [mskClock, setMskClock] = useState(() =>
        formatMoscowClock()
    );

    const knownUnreadIdsRef =
        useRef(null);

    const navigate =
        useNavigate();

    const location =
        useLocation();

    const isInstallerRoute =
        location.pathname ===
            '/installer' ||
        location.pathname.startsWith(
            '/installer/'
        );

    /*
     * Подстраницы менеджера задач
     * (/task-manager/settings, /fields, …),
     * но не главный /task-manager.
     */
    const isTaskManagerSubpage =
        location.pathname.startsWith(
            '/task-manager/'
        ) &&
        location.pathname !==
            '/task-manager/';

    const taskManagerBackPath =
        location.pathname ===
            '/task-manager/settings' ||
        location.pathname ===
            '/task-manager/settings/'
            ? '/task-manager'
            : '/task-manager/settings';

    const homePath = isInstallerRoute
        ? '/installer/work'
        : '/dashboard';

    useEffect(() => {
        ensureBrowserNotifyPermission();
    }, []);

    useEffect(() => {
        const tick = () => setMskClock(formatMoscowClock());
        tick();
        const timer = setInterval(tick, 1000);
        return () => clearInterval(timer);
    }, []);

    const fetchNotifications =
        useCallback(
            async (
                silent = false
            ) => {
                if (!silent) {
                    setNotificationLoading(
                        true
                    );
                }

                try {
                    const token =
                        localStorage.getItem(
                            'token'
                        );

                    const response =
                        await axios.get(
                            '/api/notifications',
                            {
                                params: {
                                    limit:
                                        50
                                },

                                headers: {
                                    Authorization:
                                        `Bearer ${token}`
                                }
                            }
                        );

                    const list =
                        response.data
                            ?.notifications ||
                        [];

                    const unread =
                        response.data
                            ?.unread ||
                        0;

                    const unreadIds =
                        list
                            .filter(
                                item =>
                                    !item.is_read
                            )
                            .map(
                                item =>
                                    item.id
                            );

                    if (
                        knownUnreadIdsRef.current !==
                        null
                    ) {
                        const known =
                            new Set(
                                knownUnreadIdsRef.current
                            );

                        const freshAlert =
                            list.filter(
                                item =>
                                    !item.is_read &&
                                    !known.has(
                                        item.id
                                    ) &&
                                    (
                                        item.type ===
                                            'gepard_income_discrepancy' ||
                                        item.type ===
                                            'gepard_income_ok' ||
                                        item.type ===
                                            'calendar_reminder' ||
                                        item.type ===
                                            'calendar'
                                    )
                            );

                        if (
                            freshAlert.length > 0
                        ) {
                            playNotificationBeep();

                            const first =
                                freshAlert[0];

                            showBrowserNotification({
                                title:
                                    first.title ||
                                    'Расхождение ВДР',
                                body:
                                    first.message ||
                                    '',
                                tag:
                                    `${first.type || 'notice'}-${first.id}`,
                                onClick: () => {
                                    if (
                                        first.link
                                    ) {
                                        navigate(
                                            first.link
                                        );
                                    }
                                }
                            });
                        }
                    }

                    knownUnreadIdsRef.current =
                        unreadIds;

                    setNotifications(
                        list
                    );

                    setUnreadCount(
                        unread
                    );
                } catch (error) {
                    console.error(
                        'Ошибка уведомлений:',
                        error
                    );
                } finally {
                    if (!silent) {
                        setNotificationLoading(
                            false
                        );
                    }
                }
            },
            [navigate]
        );

    const filteredNotifications = useMemo(() => {
        const now = Date.now();
        if (notificationTab === 'ok') {
            return notifications.filter(
                item =>
                    !item.is_read &&
                    item.type === OK_NOTIFICATION_TYPE
            );
        }
        if (notificationTab === 'unread') {
            return notifications.filter(
                item =>
                    !item.is_read &&
                    item.type !== OK_NOTIFICATION_TYPE
            );
        }
        if (notificationTab === 'read') {
            return notifications.filter(item =>
                Boolean(item.is_read)
            );
        }
        return notifications.filter(item => {
            if (item.is_read) return false;
            if (item.type === OK_NOTIFICATION_TYPE) return false;
            const created = new Date(
                item.created_at
            ).getTime();
            if (Number.isNaN(created)) return true;
            return now - created <= NEW_NOTIFICATION_MS;
        });
    }, [notifications, notificationTab]);

    const tabCounts = useMemo(() => {
        const now = Date.now();
        let fresh = 0;
        let unread = 0;
        let read = 0;
        let ok = 0;
        notifications.forEach(item => {
            if (item.is_read) {
                read += 1;
                return;
            }
            if (item.type === OK_NOTIFICATION_TYPE) {
                ok += 1;
                return;
            }
            unread += 1;
            const created = new Date(
                item.created_at
            ).getTime();
            if (
                Number.isNaN(created) ||
                now - created <= NEW_NOTIFICATION_MS
            ) {
                fresh += 1;
            }
        });
        return { fresh, unread, read, ok };
    }, [notifications]);

    useEffect(() => {
        fetchNotifications();

        const interval =
            setInterval(
                () => {
                    fetchNotifications(
                        true
                    );
                },
                30000
            );

        return () => {
            clearInterval(
                interval
            );
        };
    }, [
        fetchNotifications
    ]);

    const openNotification =
        async notification => {
            try {
                const token =
                    localStorage.getItem(
                        'token'
                    );

                if (
                    !notification.is_read
                ) {
                    await axios.put(
                        `/api/notifications/${
                            notification.id
                        }/read`,
                        {},
                        {
                            headers: {
                                Authorization:
                                    `Bearer ${token}`
                            }
                        }
                    );
                }

                setNotifications(
                    previous =>
                        previous.map(
                            item =>
                                item.id ===
                                notification.id
                                    ? {
                                        ...item,
                                        is_read:
                                            true
                                    }
                                    : item
                        )
                );

                setUnreadCount(
                    previous =>
                        notification.is_read
                            ? previous
                            : Math.max(
                                previous -
                                    1,
                                0
                            )
                );

                setNotificationOpen(
                    false
                );

                if (
                    notification.link
                ) {
                    // _n — чтобы повторный клик
                    // снова менял location.search
                    // и открывал задачу/сверку
                    const separator =
                        notification.link.includes(
                            '?'
                        )
                            ? '&'
                            : '?';

                    navigate(
                        `${notification.link}${separator}_n=${Date.now()}`
                    );
                }
            } catch (error) {
                message.error(
                    'Не удалось открыть уведомление'
                );
            }
        };

    const notificationBg = notification => {
        const processed =
            Boolean(
                notification.is_read ||
                    notification.payload
                        ?.processed
            );

        if (
            notification.type ===
            'gepard_income_ok'
        ) {
            return processed
                ? '#fff'
                : '#d9f7be';
        }

        if (
            notification.type ===
            'gepard_income_discrepancy'
        ) {
            return processed
                ? '#fff'
                : '#ffccc7';
        }

        return processed ? '#fff' : '#e6f4ff';
    };

    const notificationBorder = notification => {
        const processed =
            Boolean(
                notification.is_read ||
                    notification.payload
                        ?.processed
            );

        if (
            notification.type ===
                'gepard_income_ok' &&
            !processed
        ) {
            return '1px solid #b7eb8f';
        }

        if (
            notification.type ===
                'gepard_income_discrepancy' &&
            !processed
        ) {
            return '1px solid #ffa39e';
        }

        return '1px solid #f0f0f0';
    };

    const markAsProcessed = async (
        event,
        notification
    ) => {
        event.stopPropagation();
        event.preventDefault();

        try {
            const token =
                localStorage.getItem('token');

            await axios.put(
                `/api/notifications/${notification.id}/processed`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            setNotifications(previous =>
                previous.map(item =>
                    item.id === notification.id
                        ? {
                              ...item,
                              is_read: true,
                              payload: {
                                  ...(item.payload ||
                                      {}),
                                  processed: true,
                                  processed_at:
                                      new Date().toISOString()
                              }
                          }
                        : item
                )
            );

            if (!notification.is_read) {
                setUnreadCount(previous =>
                    Math.max(previous - 1, 0)
                );
            }

            message.success('Отмечено как проработано');
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Не удалось отметить как проработано'
            );
        }
    };

    const markAllRead =
        async (type = null) => {
            try {
                const token =
                    localStorage.getItem(
                        'token'
                    );

                const response =
                    await axios.put(
                        '/api/notifications/read-all',
                        type ? { type } : {},
                        {
                            headers: {
                                Authorization:
                                    `Bearer ${token}`
                            }
                        }
                    );

                setNotifications(
                    previous =>
                        previous.map(
                            item =>
                                !type ||
                                item.type === type
                                    ? {
                                          ...item,
                                          is_read:
                                              true
                                      }
                                    : item
                        )
                );

                if (type) {
                    const updated =
                        Number(
                            response.data
                                ?.updated
                        ) || 0;

                    setUnreadCount(previous =>
                        Math.max(
                            previous - updated,
                            0
                        )
                    );
                } else {
                    setUnreadCount(0);
                }
            } catch (error) {
                message.error(
                    'Не удалось отметить уведомления'
                );
            }
        };

    return (
        <div
            className={
                isInstallerRoute
                    ? 'flex h-screen bg-gray-100 overflow-hidden installer-app-shell'
                    : fullHeight
                      ? 'flex h-screen bg-gray-100 overflow-hidden'
                      : 'flex h-screen bg-gray-100'
            }
        >
            <PreviewModeBanner />
            <Sidebar
                isOpen={
                    isSidebarOpen
                }
            />

            <div
                className={
                    isInstallerRoute || fullHeight
                        ? `flex-1 flex flex-col min-h-0 min-w-0 transition-all duration-300 ${
                            isSidebarOpen
                                ? 'ml-64'
                                : 'ml-0'
                        }`
                        : `flex-1 transition-all duration-300 ${
                            isSidebarOpen
                                ? 'ml-64'
                                : 'ml-0'
                        }`
                }
            >
                <header
                    className={
                        isInstallerRoute || fullHeight
                            ? 'crm-app-header bg-white shadow px-4 py-3 flex items-center justify-between shrink-0'
                            : 'bg-white shadow px-4 py-3 flex items-center justify-between'
                    }
                >
                    <div className="flex items-center">
                        <button
                            onClick={() =>
                                setSidebarOpen(
                                    previous =>
                                        !previous
                                )
                            }
                            className="text-gray-700 hover:text-gray-900 text-2xl"
                        >
                            <MenuOutlined />
                        </button>

                        <h1 className="ml-4 text-xl font-semibold">
                            <Link to={homePath}>
                                {isInstallerRoute
                                    ? 'Кабинет'
                                    : 'Главная'}
                            </Link>
                        </h1>
                    </div>

                    <div className="flex items-center gap-4">
                        <Typography.Text
                            type="secondary"
                            style={{
                                fontVariantNumeric: 'tabular-nums',
                                minWidth: 88,
                                textAlign: 'right'
                            }}
                        >
                            МСК {mskClock}
                        </Typography.Text>

                        <Badge
                            count={
                                unreadCount
                            }
                            overflowCount={
                                99
                            }
                        >
                            <Button
                                type="text"
                                size="large"
                                icon={
                                    <BellOutlined
                                        style={{
                                            fontSize:
                                                22
                                        }}
                                    />
                                }
                                onClick={() => {
                                    setNotificationOpen(
                                        true
                                    );
                                    setNotificationTab(
                                        'new'
                                    );
                                    fetchNotifications();
                                }}
                            />
                        </Badge>
                    </div>
                </header>

                <main
                    className={
                        isInstallerRoute
                            ? 'crm-app-main installer-app-main flex-1 min-h-0 overflow-y-auto'
                            : fullHeight
                              ? 'flex-1 min-h-0 overflow-hidden p-0 flex flex-col'
                              : 'p-6 overflow-y-auto h-full'
                    }
                >
                    {isTaskManagerSubpage ? (
                        <div
                            style={{
                                marginBottom:
                                    fullHeight
                                        ? 0
                                        : 12,
                                padding:
                                    fullHeight
                                        ? '12px 16px 0'
                                        : 0
                            }}
                        >
                            <Button
                                type="text"
                                icon={
                                    <ArrowLeftOutlined />
                                }
                                onClick={() =>
                                    navigate(
                                        taskManagerBackPath
                                    )
                                }
                                style={{
                                    paddingLeft: 0,
                                    marginBottom:
                                        fullHeight
                                            ? 8
                                            : 0
                                }}
                            >
                                Назад
                            </Button>
                        </div>
                    ) : null}
                    {children}
                </main>

                {isInstallerRoute ? (
                    <InstallerBottomNav />
                ) : null}
                {isInstallerRoute ? <InstallerPhotoFab /> : null}
            </div>

            <Drawer
                title={
                    <div
                        style={{
                            display:
                                'flex',
                            justifyContent:
                                'space-between',
                            alignItems:
                                'center'
                        }}
                    >
                        <span>
                            Уведомления
                        </span>

                        {!!unreadCount && (
                            <Button
                                size="small"
                                icon={
                                    <CheckOutlined />
                                }
                                onClick={() =>
                                    markAllRead()
                                }
                            >
                                Прочитать все
                            </Button>
                        )}
                    </div>
                }
                width={480}
                open={
                    notificationOpen
                }
                onClose={() =>
                    setNotificationOpen(
                        false
                    )
                }
            >
                <Spin
                    spinning={
                        notificationLoading
                    }
                >
                    <Tabs
                        activeKey={notificationTab}
                        onChange={setNotificationTab}
                        size="small"
                        tabBarGutter={16}
                        style={{ marginBottom: 8 }}
                        items={[
                            {
                                key: 'new',
                                label: (
                                    <span>
                                        Новое
                                        {tabCounts.fresh > 0
                                            ? ` (${tabCounts.fresh})`
                                            : ''}
                                    </span>
                                )
                            },
                            {
                                key: 'unread',
                                label: (
                                    <span>
                                        Не прочитано
                                        {tabCounts.unread >
                                        0
                                            ? ` (${tabCounts.unread})`
                                            : ''}
                                    </span>
                                )
                            },
                            {
                                key: 'ok',
                                label: (
                                    <span
                                        style={{
                                            color:
                                                tabCounts.ok > 0
                                                    ? '#389e0d'
                                                    : undefined
                                        }}
                                    >
                                        Совпало
                                        {tabCounts.ok > 0
                                            ? ` (${tabCounts.ok})`
                                            : ''}
                                    </span>
                                )
                            },
                            {
                                key: 'read',
                                label: (
                                    <span>
                                        Прочитано
                                        {tabCounts.read > 0
                                            ? ` (${tabCounts.read})`
                                            : ''}
                                    </span>
                                )
                            }
                        ]}
                    />

                    {notificationTab === 'ok' &&
                        tabCounts.ok > 0 && (
                            <Button
                                block
                                icon={
                                    <CheckOutlined />
                                }
                                onClick={() =>
                                    markAllRead(
                                        OK_NOTIFICATION_TYPE
                                    )
                                }
                                style={{
                                    marginBottom: 12,
                                    color: '#389e0d',
                                    borderColor:
                                        '#b7eb8f'
                                }}
                            >
                                {`Прочитать все совпавшие (${tabCounts.ok})`}
                            </Button>
                        )}

                    {filteredNotifications.length ? (
                        <List
                            dataSource={
                                filteredNotifications
                            }
                            renderItem={
                                notification => (
                                    <List.Item
                                        onClick={() =>
                                            openNotification(
                                                notification
                                            )
                                        }
                                        style={{
                                            cursor:
                                                'pointer',

                                            padding:
                                                12,

                                            marginBottom:
                                                8,

                                            borderRadius:
                                                8,

                                            background:
                                                notificationBg(
                                                    notification
                                                ),

                                            border:
                                                notificationBorder(
                                                    notification
                                                )
                                        }}
                                    >
                                        <List.Item.Meta
                                            title={
                                                <div
                                                    style={{
                                                        display:
                                                            'flex',
                                                        justifyContent:
                                                            'space-between',
                                                        gap: 8,
                                                        alignItems:
                                                            'flex-start'
                                                    }}
                                                >
                                                    <div>
                                                        <Text
                                                            strong={
                                                                !notification.is_read
                                                            }
                                                        >
                                                            {
                                                                notification.title
                                                            }
                                                        </Text>

                                                        {!notification.is_read && (
                                                            <Tag
                                                                color={
                                                                    notification.type ===
                                                                    'gepard_income_ok'
                                                                        ? 'success'
                                                                        : notification.type ===
                                                                            'gepard_income_discrepancy'
                                                                          ? 'error'
                                                                          : 'blue'
                                                                }
                                                                style={{
                                                                    marginLeft: 8
                                                                }}
                                                            >
                                                                Новое
                                                            </Tag>
                                                        )}

                                                        {notification
                                                            .payload
                                                            ?.processed ? (
                                                            <Tag
                                                                color="default"
                                                                style={{
                                                                    marginLeft: 8
                                                                }}
                                                            >
                                                                Проработано
                                                            </Tag>
                                                        ) : null}
                                                    </div>

                                                    {(notification.type ===
                                                        'gepard_income_discrepancy' ||
                                                        notification.type ===
                                                            'gepard_income_ok') &&
                                                    !notification
                                                        .payload
                                                        ?.processed ? (
                                                        <Button
                                                            size="small"
                                                            type="primary"
                                                            ghost
                                                            icon={
                                                                <CheckCircleOutlined />
                                                            }
                                                            onClick={event =>
                                                                markAsProcessed(
                                                                    event,
                                                                    notification
                                                                )
                                                            }
                                                        >
                                                            Проработано
                                                        </Button>
                                                    ) : null}
                                                </div>
                                            }
                                            description={
                                                <>
                                                    <div>
                                                        {
                                                            notification
                                                                .message
                                                        }
                                                    </div>

                                                    <Text
                                                        type="secondary"
                                                        style={{
                                                            fontSize:
                                                                12
                                                        }}
                                                    >
                                                        {
                                                            new Date(
                                                                notification
                                                                    .created_at
                                                            )
                                                                .toLocaleString(
                                                                    'ru-RU'
                                                                )
                                                        }
                                                    </Text>
                                                </>
                                            }
                                        />
                                    </List.Item>
                                )
                            }
                        />
                    ) : (
                        <Empty
                            description={
                                notificationTab ===
                                'new'
                                    ? 'Нет новых уведомлений'
                                    : notificationTab ===
                                        'unread'
                                      ? 'Нет непрочитанных'
                                      : notificationTab ===
                                          'ok'
                                        ? 'Нет непрочитанных совпавших'
                                        : 'Нет прочитанных'
                            }
                        />
                    )}
                </Spin>
            </Drawer>
        </div>
    );
};

export default Layout;